// A puzzle's collections: a row to tick for each, and "New collection", which puts the puzzle in
// the new one. The details page shows them as they are; the finish screen and the solve screen's
// ⋯ menu open them in a dialog with the finish dialog's look (Marko, 2026-11-02).
import type { PuzzleAbout } from '@zinc/shared';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../../lib/api';
import { useCollectionMembership, useCollections } from '../../lib/library';
import { TickIcon } from '../icons';
import { Card } from '../ui';
import { CollectionDialog } from './CollectionDialog';

const ROW = 'flex min-h-12 items-center gap-3 px-3.5 text-[15px]';
const DIVIDER = 'border-b border-solid border-line-soft';

export function CollectionTicks({
  puzzleId,
  className = 'overflow-hidden',
}: {
  puzzleId: string;
  className?: string;
}) {
  const about = useQuery({
    queryKey: ['puzzle-about', puzzleId],
    queryFn: () => api<PuzzleAbout>(`/api/puzzles/${puzzleId}/about`),
  });
  const collections = useCollections();
  const membership = useCollectionMembership();
  const [creating, setCreating] = useState(false);
  const a = about.data;
  // A query waits instead of failing when the device is offline, and the solve screen can be.
  const unloaded = [about, collections].some(
    (q) => !q.data && (q.isError || q.fetchStatus === 'paused'),
  );

  return (
    <>
      <Card className={`flex flex-col ${className}`}>
        {(collections.data?.collections ?? []).map((c) => {
          const member = a?.collections.includes(c.id) ?? false;
          return (
            <button
              key={c.id}
              type="button"
              role="checkbox"
              aria-checked={member}
              disabled={!a || membership.isPending}
              onClick={() => membership.mutate({ collectionId: c.id, puzzleId, member: !member })}
              className={`${ROW} ${DIVIDER} border-0 bg-transparent text-left text-ink`}
            >
              <span
                aria-hidden="true"
                className="h-1.5 w-6 shrink-0 rounded-[3px]"
                style={{ background: c.colour }}
              />
              <span className="flex-1">{c.name}</span>
              <span className="flex w-[18px] text-accent">{member ? <TickIcon /> : null}</span>
            </button>
          );
        })}
        <button
          type="button"
          onClick={() => setCreating(true)}
          className={`${ROW} border-0 bg-transparent text-left font-semibold text-accent`}
        >
          New collection
        </button>
      </Card>
      {(unloaded || membership.isError) && (
        <p role="alert" className="m-0 text-[13px] text-mistake">
          {unloaded
            ? 'Your collections didn’t load. Check your connection and try again.'
            : 'That didn’t save. Check your connection and try again.'}
        </p>
      )}
      {creating && (
        <CollectionDialog
          onClose={() => setCreating(false)}
          onSaved={(c) => membership.mutate({ collectionId: c.id, puzzleId, member: true })}
        />
      )}
    </>
  );
}

export function AddToCollectionDialog({
  puzzleId,
  onClose,
}: {
  puzzleId: string;
  onClose: () => void;
}) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-[rgba(31,30,28,0.35)] p-3 sm:items-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby="add-to-collection-title"
      onClick={onClose}
    >
      <div
        className="flex w-full max-w-[420px] flex-col gap-3 rounded-[20px] border border-line bg-surface px-5 py-5"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id="add-to-collection-title" className="m-0 text-[20px] font-bold">
          Add to collection
        </h2>
        <CollectionTicks puzzleId={puzzleId} className="max-h-[50dvh] overflow-y-auto" />
        <button
          type="button"
          onClick={onClose}
          className="h-12 rounded-[12px] border-0 bg-accent text-[15px] font-bold text-white"
        >
          Done
        </button>
      </div>
    </div>
  );
}
